import React, { useEffect, useRef, useState } from "react";
import "./policy.scss";

import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

//assets
import zoomout from "../../assets/images/zoomOut.svg";
import zoomIn from "../../assets/images/zoomIn.svg";

import Modal from "../modal/modal";

/* ----------------------------------
 * Types
 * ---------------------------------- */

interface PolicyProps {
  handleOnclickSubmit: () => void;
  open: boolean;
  onClose: () => void;
  pdfFile?: string | File | Blob | ArrayBuffer | null;
  heading: string;
  isPolicyFirst: boolean;
}

const Policy: React.FC<PolicyProps> = ({
  handleOnclickSubmit,
  open,
  onClose,
  pdfFile,
  heading,
  isPolicyFirst,
}) => {
  const [checked, setChecked] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1.0);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const modalRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const PLEASE_SCROLL_TO_BOTTOM_NOTE =
    "Please scroll to the bottom of the document to enable the acceptance button.";

  /* ----------------------------------
   * Handlers
   * ---------------------------------- */

  // ✅ Correct typing (NO PDFDocumentProxy)
  const onDocumentLoadSuccess = ({
    numPages,
  }: {
    numPages: number;
  }): void => {
    setNumPages(numPages);
  };

  const handleZoomIn = (): void =>
    setZoom((prev) => Math.min(prev + 0.2, 3));

  const handleZoomOut = (): void =>
    setZoom((prev) => Math.max(prev - 0.2, 0.5));

  /* ----------------------------------
   * Effects
   * ---------------------------------- */

  useEffect(() => {
    if (open) {
      setZoom(1.0);
      setScrolledToBottom(false);
      setChecked(false);
      document.body.classList.add("policy-open");
    } else {
      document.body.classList.remove("policy-open");
    }

    return () => {
      document.body.classList.remove("policy-open");
    };
  }, [open]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent): void => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [open, onClose]);

  /* ----------------------------------
   * Scroll detection
   * ---------------------------------- */

  const handleScroll = (): void => {
    if (!scrollRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } =
      scrollRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setScrolledToBottom(true);
    }
  };

  /* ----------------------------------
   * Button enable logic
   * ---------------------------------- */

  const isButtonEnabled = isPolicyFirst
    ? scrolledToBottom && checked
    : true;

  /* ----------------------------------
   * Render
   * ---------------------------------- */

  return (
    <Modal open={open} onClose={onClose}>
      <div className="policy-modal-overlay">
        <div className="policy-modal-content" ref={modalRef}>
          <div className="policy-modal-header">
            <div className="heading-title">{heading}</div>

            <div className="zoom-buttons">
              <button
                type="button"
                onClick={handleZoomOut}
                className="zoom-btn"
              >
                <img src={zoomout} alt="zoom-out" />
              </button>

              <button
                type="button"
                onClick={handleZoomIn}
                className="zoom-btn"
              >
                <img src={zoomIn} alt="zoom-in" />
              </button>
            </div>
          </div>

          <div
            className="pdf-scrollable-wrapper"
            ref={scrollRef}
            onScroll={handleScroll}
          >
            <div className="pdf-viewer-container">
              {pdfFile ? (
                <Document
                  file={pdfFile}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<p>Loading PDF...</p>}
                  error={<p>Failed to load PDF.</p>}
                >
                  {numPages &&
                    Array.from(
                      { length: numPages },
                      (_, index) => (
                        <Page
                          key={`page_${index + 1}`}
                          pageNumber={index + 1}
                          scale={zoom}
                          width={Math.min(
                            window.innerWidth - 40,
                            650
                          )}
                        />
                      )
                    )}
                </Document>
              ) : (
                <p>No PDF to display</p>
              )}
            </div>
          </div>

          <div className="policy-footer">
            {isPolicyFirst && (
              <label className="policy-checkbox-label">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) =>
                    setChecked(e.target.checked)
                  }
                  className="policy-checkbox"
                />
                <span>I have read and agree to the policy</span>
              </label>
            )}

            <div className="button-container">
              <button
                disabled={!isButtonEnabled}
                onClick={handleOnclickSubmit}
                className="submit-policy-btn"
                style={{
                  backgroundColor: !isButtonEnabled
                    ? "#F2B79E"
                    : "#FD8238",
                  color: "#fff",
                }}
              >
                {isPolicyFirst
                  ? "Accept & Continue"
                  : "Close"}
              </button>
            </div>

            {isPolicyFirst && (
              <div className="policy-note-text">
                {PLEASE_SCROLL_TO_BOTTOM_NOTE}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default Policy;
